import React, { useCallback, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { isEqual, omit } from "lodash"
import moment from "moment"
import { DATE_FORMAT } from "modules/datepicker/constants"
import { getCrewMemberCalendar } from "store/crewMemberCalendar"
import { useCalendarDateRange } from "pagesV2/pro/Calendar/useCalendarDateRange"
import { routes } from "router"
import Calendar from "ui/Calendar"
import useSearchFilter from "hooks/useSearchFilter"
import Journal from "pagesV2/pro/Calendar/Modals/Journal"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

/**
 * Crew Member Calendar Wrapper
 * Fetches calendar data for a specific crew member via dedicated API endpoint
 * Uses separate Redux store (crewMemberCalendar) to avoid conflicts with main calendar
 */
const CrewMemberCalendar = ({ crewMemberId }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)

  const [params, updateSearchFilters] = useSearchFilter({
    accessAttributes: ["range", "date"]
  })
  const [paramsState, setParamsState] = useState(params)
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [selectedJournalDate, setSelectedJournalDate] = useState(null)

  // Ensure params always have default values for the hook
  const paramsWithDefaults = {
    ...params,
    date: params.date || moment().format(DATE_FORMAT),
    range: params.range || "week"
  }

  const dates = useCalendarDateRange(paramsWithDefaults)

  const { bookings, loading, timeOff, journalNoteDates } = useSelector((state) => state.crewMemberCalendar)

  const changeHandler = useCallback(
    ({ target, extra }) =>
      setParamsState((prevParamsState) => ({
        ...prevParamsState,
        [target.name]: target.value,
        ...(extra && { [extra.name]: extra.value })
      })),
    []
  )

  useEffect(() => {
    if (isEqual(paramsState, omit(params, []))) return
    updateSearchFilters(paramsState)
  }, [params, paramsState, updateSearchFilters])

  // Fetch calendar data when date range changes
  useEffect(() => {
    if (!dates?.start || !dates?.end) return

    const data = {
      "q[date_gteq]": dates.start,
      "q[date_lteq]": dates.end
    }

    dispatch(getCrewMemberCalendar(crewMemberId, data))
  }, [dates?.start, dates?.end, crewMemberId, dispatch])

  const handleAddBooking = () => {
    navigate(routes.guideBookingNewPath())
  }

  const handleJournalClick = (date) => {
    setSelectedJournalDate(date)
    setIsJournalModalOpen(true)
  }

  const handleJournalCancel = () => {
    setIsJournalModalOpen(false)
    setSelectedJournalDate(null)
  }

  return (
    <>
      <Calendar
        variant="crew"
        bookingsData={bookings || []}
        journalNoteDates={journalNoteDates || []}
        timeOffData={timeOff || []}
        loading={loading}
        date={paramsWithDefaults.date}
        range={paramsWithDefaults.range}
        onChangeFilters={changeHandler}
        onAddBooking={handleAddBooking}
        onJournalClick={handleJournalClick}
        canCreateJournal={canCreateJournal}
        canEditJournal={canEditJournal}
        canViewJournal={canViewJournal}
      />
      {isJournalModalOpen && selectedJournalDate && (
        <Journal
          open={isJournalModalOpen}
          setOpen={setIsJournalModalOpen}
          onCancel={handleJournalCancel}
          selectedDate={selectedJournalDate}
          startDate={dates?.start}
          endDate={dates?.end}
          context="crew_member"
          crewMemberId={crewMemberId}
        />
      )}
    </>
  )
}

CrewMemberCalendar.propTypes = {
  crewMemberId: PropTypes.string.isRequired
}

export default CrewMemberCalendar
